import { FC, useState } from 'react';
import styled from '@emotion/styled';
import H2 from '@base/H2/H2';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Divider from '@base/Divider/Divider';
import AddCircleOutline from '@icon/AddCircleOutline';
import RemoveCircleOutline from '@icon/RemoveCircleOutline';
import useWindowSize from '@hooks/useWindowSize';
import { Spacings } from '@styles/index';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 10;
`;

const ListContainer = styled.div`
  margin-top: ${Spacings.SPACING_5B};
  margin-bottom: ${Spacings.SPACING_10B};
  @media (max-width: 47.95em) {
    margin-top: 24px;
  }
`;

const QuestionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${Spacings.SPACING_6B};
  padding-bottom: ${Spacings.SPACING_4B};
  cursor: pointer;
  @media (max-width: 47.95em) {
    padding: 16px 0 12px;
  }
`;

const Question = styled(H4)`
  flex: 0.9;
  @media (max-width: 47.95em) {
    font-size: 16px;
    font-weight: 600;
    line-height: 150%;
    flex: 0.98;
  }
`;

const IconContainer = styled.div`
  flex: 0.1;
  flex-direction: column;
  align-items: center;
  @media (max-width: 47.95em) {
    flex: 0;
  }
`;

const StyledAnswer = styled(P1)`
  margin-bottom: ${Spacings.SPACING_6B};
  @media (max-width: 47.95em) {
    margin-bottom: 16px;
  }
`;

const Title = styled(H2)`
  @media (max-width: 47.95em) {
    font-size: 22px;
  }
`;

type FAQModalProps = {
  data: any[];
};

const Collapse = styled.div`
  max-height: 0;
  width: 100%;
  overflow: hidden;
  transition: max-height 0.25s ease-out;

  &.expanded {
    max-height: 150px;
    overflow: auto;
    transition: max-height 0.25s ease-in;
  }
`;

const FAQModal: FC<FAQModalProps> = ({ data }) => {
  const [expandedIndex, setExpandedIndex] = useState<number>(-1);
  const { isMobile } = useWindowSize();

  const handlePlusClick = (index: number) => {
    if (index !== expandedIndex) {
      setExpandedIndex(index);
    } else {
      setExpandedIndex(-1);
    }
  };
  const iconSize = isMobile ? '18px' : '24px';
  return (
    <ModalContent>
      <Title>FAQs</Title>
      <ListContainer>
        {data.map((dataItem, index) => {
          const { question, answer } = dataItem;
          const isLast = index === data.length - 1;
          const isExpanded = expandedIndex === index;
          return (
            <div key={String(index)}>
              <QuestionContainer onClick={() => handlePlusClick(index)}>
                <Question>{question}</Question>
                <IconContainer>
                  {isExpanded ? (
                    <RemoveCircleOutline
                      size={iconSize}
                      color="var(--color-text-primary)"
                    />
                  ) : (
                    <AddCircleOutline
                      size={iconSize}
                      color="var(--color-text-primary)"
                    />
                  )}
                </IconContainer>
              </QuestionContainer>
              <Collapse className={isExpanded ? 'expanded' : ''}>
                <StyledAnswer>{answer}</StyledAnswer>
              </Collapse>

              {!isLast && <Divider />}
            </div>
          );
        })}
      </ListContainer>
    </ModalContent>
  );
};

export default FAQModal;
